import moment, { Moment } from 'moment';
import { agent } from '@shared/services/agent';
import { AccountType } from '@shared/models/company.models';

export class AccountInfo {
  constructor(
    public quantity: string,
    public endDateString: string,
    public totalUsers: number,
    public active: boolean,
    public hasSubscription: boolean,
    public userLimit: number,
    public billTotal: number,
    public dueDateString: string,
    public teamLimit: number,
    public totalTeams: number,
    public usesStripe: boolean,

    public isFreeTrial: boolean,
    public paymentFrequency: PaymentFrequency,
    public enableHolidayBooking: boolean,
    public accountType?: AccountType,
    public freeUsers?: number,
    public paidUsers?: number,
    public customWhereaboutsCount?: number,
    public meetingRoomsCount?: number,
    public unitAmount?: number,
    public currency?: string,
  ) {
  }

  public getEndDate(): Moment {
    return moment(this.endDateString);
  }

  public getDueDate(): Moment {
    return moment(this.dueDateString);
  }
}

// Limits and counts every user gets (GET /api/v2/config and /api/stripe/account-summary). The full
// AccountInfo, with bill amounts and renewal dates, is company-admin only and loaded by the payments page.
export interface AccountSummary {
  userLimit: number;
  totalUsers: number;
  teamLimit: number;
  totalTeams: number;
  enableHolidayBooking: boolean;
  paidAccount: boolean;
}

export function toAccountSummary(accountInfo: AccountInfo): AccountSummary {
  return {
    userLimit: accountInfo.userLimit,
    totalUsers: accountInfo.totalUsers,
    teamLimit: accountInfo.teamLimit,
    totalTeams: accountInfo.totalTeams,
    enableHolidayBooking: accountInfo.enableHolidayBooking,
    paidAccount: (accountInfo.billTotal ?? 0) > 0,
  };
}

export function hasUsedTeamAllocation(accountSummary?: AccountSummary): boolean {
  if (!accountSummary) return false;
  return accountSummary.totalTeams >= accountSummary.teamLimit;
}

export async function getAccountSummary(): Promise<AccountSummary> {
  return await agent.requests.get('/api/stripe/account-summary');
}

export enum PaymentFrequency {
  Monthly = 'MONTHLY', Yearly = 'YEARLY'
}

export async function getAccountInfo(): Promise<AccountInfo> {
  const response = await agent.requests.get('/api/stripe/account-info');
  return new AccountInfo(
    response.quantity,
    response.endDate,
    response.totalUsers,
    response.active,
    response.hasSubscription,
    response.userLimit,
    response.billTotal,
    response.dueDate,
    response.teamLimit,
    response.totalTeams,
    response.usesStripe,
    response.freeTrial,
    response.paymentFrequency,
    response.enableHolidayBooking,
    response.accountType,
    response.freeUsers,
    response.paidUsers,
    response.customWhereaboutsCount,
    response.meetingRoomsCount,
    response.unitAmount,
    response.currency,
  )
}

export interface PaymentInfo {
  lastFour: string;
  cardExpiryDate: string;
}

